/**
 * Readable Search JSON — public API. Converts between the flat filter store
 * (`SearchBrowseParams`) and a human/AI-readable JSON payload used by the Search
 * Payload dialog. See docs/copy-apply-search-payload.md.
 */
import type { SearchTableScope } from '@aibees/elephant/store/search-params-store';
import type { SearchBrowseParams } from '@aibees/elephant/types';
import { jsonStringify } from '@aibees/shared/utils';
import { paramsToReadable } from './present';
import { readableToParams, validateReadable } from './parse';

export { paramsToReadable } from './present';
export { readableToParams, validateReadable } from './parse';

/** Pretty-printed readable JSON for the current scope's params. */
export function toReadableJsonString(
  scope: SearchTableScope,
  params: Partial<SearchBrowseParams>
): string {
  return jsonStringify(paramsToReadable(scope, params), 2) ?? '{}';
}

export type ParseReadableResult =
  | { ok: true; params: Partial<SearchBrowseParams> }
  | { ok: false; errors: string[] };

/**
 * Parse + validate an edited/pasted readable JSON string into store params.
 * Structural validation only (invalid JSON, wrong shape, unknown fields).
 */
export function parseReadableJsonString(
  scope: SearchTableScope,
  text: string
): ParseReadableResult {
  let json: unknown;
  try {
    json = JSON.parse(text);
  } catch (e) {
    return { ok: false, errors: [`Invalid JSON: ${(e as Error).message}`] };
  }
  const errors = validateReadable(scope, json);
  if (errors.length) return { ok: false, errors };
  return {
    ok: true,
    params: readableToParams(scope, json as Record<string, unknown>),
  };
}
